import { useCallback, useEffect, useState } from 'react';

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useActiveContacts } from '@/hooks/useActiveContacts';
import { useMessagingSocket } from '@/hooks/useMessagingSocket';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useSidebarStore } from '@/stores/sidebar.store';

import { ContactListPane } from './components/ContactListPane';
import { ContactThreadPane } from './components/ContactThreadPane';
import {
  MultiChatGrid,
  addMultiChatId,
  addMultiChatIdToFront,
  getMultiChatIds,
} from './components/MultiChatGrid';

const VIEW_MODE_KEY = 'contacts-view-mode';

type ViewMode = 'single' | 'multi';

const getSavedViewMode = (): ViewMode => {
  const saved = localStorage.getItem(VIEW_MODE_KEY);
  return saved === 'multi' ? 'multi' : 'single';
};

export const ContactsPage = () => {
  usePageTitle('Sales Console');
  useMessagingSocket();
  useActiveContacts();
  const { contactId } = useParams<{ contactId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>(getSavedViewMode);
  const [multiChatIds, setMultiChatIds] = useState<string[]>(getMultiChatIds);
  const autoCollapse = useSidebarStore((s) => s.autoCollapse);
  const restoreCollapsed = useSidebarStore((s) => s.restoreCollapsed);

  // Deep links must work in multi view too: /contacts/:id adds that contact
  // to the front of the grid instead of being ignored.
  useEffect(() => {
    if (viewMode === 'multi' && contactId) {
      const updated = addMultiChatIdToFront(contactId);
      setMultiChatIds([...updated]);
    }
  }, [viewMode, contactId]);

  // Auto-collapse sidebar when entering multi-grid mode, restore when leaving
  useEffect(() => {
    if (viewMode === 'multi') {
      autoCollapse();
    } else {
      restoreCollapsed();
    }
  }, [viewMode, autoCollapse, restoreCollapsed]);

  // Restore sidebar state when unmounting (leaving the contacts page)
  useEffect(() => {
    return () => {
      restoreCollapsed();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleViewMode = () => {
    const next = viewMode === 'single' ? 'multi' : 'single';
    setViewMode(next);
    localStorage.setItem(VIEW_MODE_KEY, next);
  };

  const handleSelect = useCallback(
    (id: string) => {
      if (viewMode === 'multi') {
        const updated = addMultiChatId(id);
        setMultiChatIds([...updated]);
      } else {
        const qs = searchParams.toString();
        navigate(`/sales-console/${id}${qs ? `?${qs}` : ''}`);
      }
    },
    [navigate, searchParams, viewMode],
  );

  return (
    <div className="-m-8 flex h-screen overflow-hidden bg-white dark:bg-[#111b21]">
      <div className="w-[280px] shrink-0 border-r border-[#d1d7db] dark:border-[#2a3942]">
        <ContactListPane
          selectedId={viewMode === 'single' ? contactId : undefined}
          selectedIds={viewMode === 'multi' ? multiChatIds : undefined}
          onSelect={handleSelect}
          viewMode={viewMode}
          onToggleViewMode={toggleViewMode}
        />
      </div>
      <div className="min-w-0 flex-1">
        {viewMode === 'single' ? (
          <ContactThreadPane contactId={contactId} />
        ) : (
          <MultiChatGrid contactIds={multiChatIds} onContactIdsChange={setMultiChatIds} />
        )}
      </div>
    </div>
  );
};
